import type { TileConfigPrice } from './hooks/useTileConfigPrices';

import { memo } from 'react';

import { useTileConfigPrices } from './hooks/useTileConfigPrices';
import { S } from './TileTitleWithPrice';
import { useTileBaseContext } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';

const CONFIG_PRICE_TEST_IDS: Record<TileConfigPrice['type'], string> = {
  rrp: 'product-tile-initial-price',
  itp: 'product-tile-intermediary-price',
  l30d: 'product-tile-lowest-price',
};

type TilePriceConfigProps = {
  discountedColor?: string;
};

/**
 * Product tile price block for `LowestPriceDisplay.CONFIG`,
 * resolved by the product `priceDisplayMode`
 */
const TilePriceConfigComponent = ({ discountedColor }: TilePriceConfigProps) => {
  const { priceFormatter } = useStaticContext();
  const { singleProduct } = useTileBaseContext();
  const { tileType } = useTileContext();

  const { priceFormatted, priceValue } = useMembersOnlyPrices({
    price: singleProduct.priceValue,
    personalisedPrice: singleProduct.personalisedPriceValue,
  });

  const configPrices = useTileConfigPrices({
    priceDisplayMode: singleProduct.priceDisplayMode,
    priceValue,
    fromPriceValue: singleProduct.fromPriceValue,
    intermediaryPriceValue: singleProduct.intermediaryPriceValue,
    lowestPrice: singleProduct.lowestPrice,
    hideIntermediaryPrice: tileType === 'ctl',
  });
  const hasDiscount = (singleProduct.fromPriceValue || 0) > (priceValue || 0);

  if (!priceFormatted) {
    return null;
  }

  return (
    <S.PriceWrapper>
      {configPrices.map(({ type, value, strikethrough }) => (
        <S.PriceLowest30Days
          key={type}
          data-testid={CONFIG_PRICE_TEST_IDS[type]}
          data-strikethrough={strikethrough}
          data-cs-capture=""
        >
          {priceFormatter(value)}
        </S.PriceLowest30Days>
      ))}
      {hasDiscount ? (
        <S.DiscountedPrice
          $color={discountedColor}
          data-testid="product-tile-price"
          data-cs-capture=""
        >
          {priceFormatted}
        </S.DiscountedPrice>
      ) : (
        <S.InitialPrice data-testid="product-tile-price" data-cs-capture="">
          {priceFormatted}
        </S.InitialPrice>
      )}
    </S.PriceWrapper>
  );
};

export const TilePriceConfig = memo(TilePriceConfigComponent);
